/**
 * Pure, React-free localStorage accessors for the Health module's onboarding /
 * view-state flags (#5579, first finding).
 *
 * These used to be raw, unprefixed, unscoped keys (`health-active-tab`,
 * `health-consent-dismissed`, `health-wearable-skipped`) shared by EVERY
 * workspace and account on a browser — dismissing the consent banner or the
 * "use health tracking without a wearable" CTA in one workspace silently hid
 * onboarding in a brand-new workspace/account on the same browser, with the
 * cause invisible to the user.
 *
 * Keys are now `xavior:`-prefixed and scoped per module instance (a module's
 * `id` is unique per workspace/conversation row, so scoping by it also closes
 * the cross-workspace/cross-account leak) — mirrors the localStorage
 * precedent already used for this class of "last view" preference elsewhere
 * (`xavior:calendar_time_filter_<moduleId>`, `xavior:kanban.activeBoard:<moduleId>`,
 * `xavior:contacts.view:<moduleId>`). Those all stay in localStorage rather
 * than sessionStorage on purpose — the preference is meant to survive across
 * browser sessions, unlike an ephemeral in-tab nav position.
 *
 * `activeTab` keeps a one-time legacy-key fallback (read only when the new
 * key has never been written, never deletes the legacy key) that ALSO
 * writes the resolved value forward to the new per-module key immediately —
 * safe here because inheriting a stray "last active tab" across
 * workspaces/accounts is a harmless UI nicety, not a data-scoping concern.
 *
 * `consentDismissed`/`wearableSkipped` do NOT get a legacy fallback (review
 * follow-up on the #5579 HIGH finding): a fallback that writes the resolved
 * value forward would propagate the leaked GLOBAL legacy value into every
 * NEW module's key on first read — structurally reproducing the exact
 * cross-workspace/account inheritance #5579 was filed to close, just moved
 * one layer down. Unset reads as `false`, so every module/workspace shows
 * the consent banner / wearable prompt once and writes its own key from
 * then on; a legacy-dismissed user sees a one-time re-prompt, which is the
 * correct trade-off for actually closing the leak.
 */

export type HealthActiveTab = 'log' | 'trends' | 'profile' | 'labs';

const LEGACY_ACTIVE_TAB_KEY = 'health-active-tab';

export function healthActiveTabStorageKey(moduleId: string): string {
  return `xavior:health.active-tab:${moduleId}`;
}

export function healthConsentDismissedStorageKey(moduleId: string): string {
  return `xavior:health.consent-dismissed:${moduleId}`;
}

export function healthWearableSkippedStorageKey(moduleId: string): string {
  return `xavior:health.wearable-skipped:${moduleId}`;
}

function isHealthActiveTab(value: unknown): value is HealthActiveTab {
  return value === 'log' || value === 'trends' || value === 'profile' || value === 'labs';
}

/** Read the persisted active tab: the new per-module key first, falling back
 *  to the legacy global key only when the new one has never been written. A
 *  fallback hit writes the resolved value forward to the new key so the
 *  migration completes on this very read, not just on the next tab switch. */
export function readHealthActiveTab(moduleId: string): HealthActiveTab {
  try {
    const key = healthActiveTabStorageKey(moduleId);
    const next = localStorage.getItem(key);
    if (next !== null) return isHealthActiveTab(next) ? next : 'log';
    const legacyRaw = localStorage.getItem(LEGACY_ACTIVE_TAB_KEY);
    if (legacyRaw === null) return 'log';
    const value = isHealthActiveTab(legacyRaw) ? legacyRaw : 'log';
    try { localStorage.setItem(key, value); } catch { /* best-effort */ }
    return value;
  } catch {
    return 'log';
  }
}

export function writeHealthActiveTab(moduleId: string, tab: HealthActiveTab): void {
  try {
    localStorage.setItem(healthActiveTabStorageKey(moduleId), tab);
  } catch {
    /* storage unavailable/full — best-effort */
  }
}

function writeRawFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    /* storage unavailable/full — best-effort */
  }
}

/** Read a per-module boolean flag from ONLY its new key — deliberately NO
 *  legacy-key fallback (see the file header). Unset (or storage-unavailable)
 *  reads as `false`. */
function readBoolFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeBoolFlag(key: string): void {
  writeRawFlag(key, true);
}

export function readHealthConsentDismissed(moduleId: string): boolean {
  return readBoolFlag(healthConsentDismissedStorageKey(moduleId));
}

export function writeHealthConsentDismissed(moduleId: string): void {
  writeBoolFlag(healthConsentDismissedStorageKey(moduleId));
}

export function readHealthWearableSkipped(moduleId: string): boolean {
  return readBoolFlag(healthWearableSkippedStorageKey(moduleId));
}

export function writeHealthWearableSkipped(moduleId: string): void {
  writeBoolFlag(healthWearableSkippedStorageKey(moduleId));
}
